import React, { useState } from "react";
import axios from "axios";
import { MapPin, Mail, Phone, Send, CheckCircle2, Building2, AlertCircle, Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import MapSedi from "../components/MapSedi";
import useReveal from "../hooks/useReveal";
import { images } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contatti() {
  const { t } = useLang();
  useReveal([t]);
  const c = t.contact;
  const [form, setForm] = useState({ nome: "", email: "", tipo: "", messaggio: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ nome: "", email: "", tipo: "", messaggio: "" });
      setTimeout(() => setStatus("idle"), 6000);
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <main>
      <Seo
        title="Contatti | MichelChef® S.r.l. — Sedi e informazioni"
        description="Contatta MichelChef®: laboratorio a Guarda Veneta (RO), sede legale a Riva del Po (FE), show room a Calderara di Reno (BO). Tel +39 0425 544695."
        path="/contatti"
        image={images.dish2}
      />
      <PageHeader crumb={c.crumb} eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} image={images.chefsKitchen} homeLabel={t.common.home} />

      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14">
          <div className="reveal">
            <h2 className="font-display font-extrabold text-3xl text-[#14110F]">{c.formTitle}</h2>
            <p className="mt-3 text-[#3B342E]/75">{c.formSub}</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#14110F] mb-2">{c.nome}</label>
                  <input name="nome" value={form.nome} onChange={handle} required placeholder={c.nomePh}
                    className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-[#17784A] focus:ring-2 focus:ring-[#17784A]/20 transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#14110F] mb-2">{c.email}</label>
                  <input name="email" type="email" value={form.email} onChange={handle} required placeholder={c.emailPh}
                    className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-[#17784A] focus:ring-2 focus:ring-[#17784A]/20 transition" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#14110F] mb-2">{c.tipo}</label>
                <select name="tipo" value={form.tipo} onChange={handle} required
                  className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-[#17784A] focus:ring-2 focus:ring-[#17784A]/20 transition">
                  <option value="" disabled>{c.tipoPlaceholder}</option>
                  {c.tipoOpts.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#14110F] mb-2">{c.messaggio}</label>
                <textarea name="messaggio" value={form.messaggio} onChange={handle} required rows={5} placeholder={c.msgPh}
                  className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-[#17784A] focus:ring-2 focus:ring-[#17784A]/20 transition resize-none" />
              </div>
              <button type="submit" disabled={status === "sending"}
                className="btn-press inline-flex items-center gap-2 rounded-full bg-[#17784A] text-white px-7 py-3.5 font-semibold hover:bg-[#13633c] disabled:opacity-70">
                {status === "sending" ? (<><Loader2 size={17} className="animate-spin" /> {c.sending}</>) : (<>{c.send} <Send size={17} /></>)}
              </button>
              {status === "sent" && (
                <div className="flex items-center gap-2 text-[#17784A] font-medium mt-2"><CheckCircle2 size={20} /> {c.sent}</div>
              )}
              {status === "error" && (
                <div className="flex items-center gap-2 text-[#D8382E] font-medium mt-2"><AlertCircle size={20} /> {c.error}</div>
              )}
            </form>
          </div>

          <div className="reveal reveal-delay-1 space-y-5">
            <div className="rounded-2xl bg-[#14110F] text-white p-7">
              <h3 className="font-display font-bold text-xl">{c.directTitle}</h3>
              <div className="mt-5 space-y-4">
                <a href="mailto:info@michelchef.it" className="flex items-center gap-3 text-white/80 hover:text-white transition">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-white/10"><Mail size={20} /></span>
                  info@michelchef.it
                </a>
                <a href="tel:+390425544695" className="flex items-center gap-3 text-white/80 hover:text-white transition">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-white/10"><Phone size={20} /></span>
                  +39 0425 544695
                </a>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {t.locations.map((loc, i) => (
                <div key={i} className="rounded-2xl bg-white border border-black/5 shadow-sm p-6 card-lift">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#17784A]/10 text-[#17784A]"><Building2 size={20} /></span>
                  <h4 className="font-display font-bold mt-4 text-[#14110F] text-sm">{loc.type}</h4>
                  <div className="mt-2 flex gap-2 text-sm text-[#3B342E]/75 leading-relaxed">
                    <MapPin size={16} className="text-[#D8382E] mt-0.5 shrink-0" />
                    <span>{loc.address}<br />{loc.city}<br />{loc.region}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="reveal mt-16">
          <h3 className="font-display font-extrabold text-2xl lg:text-3xl text-[#14110F] mb-6">{c.mapTitle}</h3>
          <MapSedi locations={t.locations} />
        </div>
      </section>
    </main>
  );
}
